import { productFacts } from './productFacts';

export type LandingCard = {
  icon: string;
  title: string;
  body: string;
};

export type LandingProof = {
  company: string;
  descriptor: string;
  quote: string;
  detail: string;
  logo: string;
  logoWidth: number;
  logoHeight: number;
  href?: string;
};

export type LandingComparison = {
  competitor: string;
  officialUrl: string;
  sourceLabel: string;
  reviewed: string;
  rows: Array<{ topic: string; chatCo: string; competitor: string }>;
};

export type SearchLandingPageConfig = {
  path: string;
  breadcrumb: string;
  eyebrow: string;
  title: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  heroBullets: string[];
  sampleQuestion: string;
  sampleAnswer: string;
  challengeTitle: string;
  challengeDescription: string;
  challenges: LandingCard[];
  capabilityTitle: string;
  capabilityDescription: string;
  capabilities: LandingCard[];
  applications: string[];
  faqs: Array<{ question: string; answer: string }>;
  related: Array<{ label: string; href: string }>;
  proof?: LandingProof;
  comparison?: LandingComparison;
};

const sharedFaqs = {
  trial: {
    question: 'Is there a free plan?',
    answer: productFacts.trial,
  },
};

const maxetaProof: LandingProof = {
  company: 'MAXeta Energy',
  descriptor: 'Energy consulting & regulatory strategy',
  quote:
    'Case-specific research that used to take our analysts hours now takes minutes — and every answer comes with the record citation to back it up.',
  detail:
    'MAXeta uses dedicated Energy Expert Agents across large regulatory records, with answers traceable to the source document.',
  logo: '/images/landing/logos/maxeta.png',
  logoWidth: 703,
  logoHeight: 200,
  href: '/case-studies/maxeta-energy',
};

const calvarioProof: LandingProof = {
  company: 'Calvario City Christian Academy & Pre-School',
  descriptor: 'Private K-12 education',
  quote:
    'We used to print thousands of pages of teacher manuals every year. Now our faculty gets instant, cited answers through our own Faculty Portal.',
  detail:
    'Calvario replaced printed manuals with an access-restricted portal grounded in current school policies and procedures.',
  logo: '/images/landing/logos/calvario.png',
  logoWidth: 633,
  logoHeight: 200,
  href: '/case-studies/calvario-academy',
};

const sosaArveloProof: LandingProof = {
  company: 'Sosa & Arvelo',
  descriptor: 'Legal, regulatory, strategy & risk work',
  quote:
    'Our team went from spending hours digging through client files to getting actionable, sourced insights in minutes.',
  detail:
    'Sosa & Arvelo uses grounded knowledge workflows to make client and regulatory material easier to find and verify.',
  logo: '/images/landing/logos/sosa-arvelo.png',
  logoWidth: 355,
  logoHeight: 200,
};

export const searchLandingPages = {
  aiChatbotForWebsite: {
    path: '/ai-chatbot-for-website',
    breadcrumb: 'AI Chatbot for Website',
    eyebrow: 'AI chatbot for your website',
    title: 'Turn your website and documents into a cited AI agent',
    description:
      'Answer visitor questions around the clock, capture qualified leads, and hand conversations to your team—with responses grounded in the content you approve.',
    seoTitle: 'AI Chatbot for Your Website with Citations | Chat.co',
    seoDescription:
      'Build an AI chatbot for your website from your pages and documents. Give cited answers, capture leads, and start with a 14-day free trial.',
    heroBullets: [
      'Train on pages, documents, text, and Q&A pairs',
      'Embed with a script or iframe—no redesign required',
      'Capture leads and support human handoff workflows',
    ],
    sampleQuestion: 'Which plan includes private team access?',
    sampleAnswer:
      'Business and Enterprise support advanced access options. Here are the plan details and source links.',
    challengeTitle:
      'A generic bot creates more work. A grounded agent resolves the right questions.',
    challengeDescription:
      'Visitors expect a direct answer, while your team needs control over what the agent can say and where the answer came from.',
    challenges: [
      {
        icon: 'tabler:clock-question',
        title: 'Repeated questions',
        body: 'Sales and support teams answer the same product, policy, and onboarding questions every day.',
      },
      {
        icon: 'tabler:file-search',
        title: 'Buried answers',
        body: 'The answer exists across web pages and PDFs, but visitors cannot find it quickly.',
      },
      {
        icon: 'tabler:message-off',
        title: 'Unverifiable output',
        body: 'A fluent answer without a source creates risk and sends your team back to manual checking.',
      },
    ],
    capabilityTitle: 'Launch a website agent your team can control',
    capabilityDescription:
      'Chat.co brings knowledge, deployment, lead capture, and conversation review into one no-code workflow.',
    capabilities: [
      {
        icon: 'tabler:world-www',
        title: 'Website-grounded answers',
        body: 'Crawl approved pages and combine them with documents, text, and curated Q&A.',
      },
      {
        icon: 'tabler:quote',
        title: 'Source citations',
        body: 'Let visitors and staff open the supporting source when an answer needs verification.',
      },
      {
        icon: 'tabler:forms',
        title: 'Lead capture',
        body: 'Collect contact details after a configurable point in the conversation.',
      },
      {
        icon: 'tabler:paint',
        title: 'Brand controls',
        body: 'Customize colors, welcome messages, prompts, placement, and agent personality.',
      },
    ],
    applications: [
      'Product questions',
      'Policy guidance',
      'Lead qualification',
      'Customer onboarding',
      'Multilingual visitor support',
      'After-hours answers',
    ],
    faqs: [
      {
        question: 'How do I add Chat.co to my website?',
        answer:
          'Publish with the JavaScript embed, an iframe, a direct link, or a QR code. The website integration guide provides the implementation steps.',
      },
      {
        question: 'Can the agent answer only from my content?',
        answer:
          'You control the approved knowledge sources and agent instructions. Citations make grounded answers inspectable, although important answers should still be checked against the source.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'Customer support AI agent', href: '/customer-support-ai-agent' },
      { label: 'AI knowledge base', href: '/ai-knowledge-base' },
      { label: 'Website integration guide', href: '/docs/website-integration' },
    ],
  },
  aiKnowledgeBase: {
    path: '/ai-knowledge-base',
    breadcrumb: 'AI Knowledge Base',
    eyebrow: 'AI knowledge base',
    title: 'Ask your knowledge base a question. Get the answer and the source.',
    description:
      'Turn documents, websites, Q&A pairs, and connected data into a searchable AI knowledge layer for customers, employees, or specialist research teams.',
    seoTitle: 'AI Knowledge Base with Source Citations | Chat.co',
    seoDescription:
      'Create an AI knowledge base from documents, websites, and connected sources. Deliver cited answers for customers and teams with Chat.co.',
    heroBullets: [
      'One knowledge layer across multiple source types',
      'Cited answers that link back to supporting material',
      'Use leading AI models without rebuilding your sources',
    ],
    sampleQuestion: 'What changed in the latest operating procedure?',
    sampleAnswer:
      'The approval threshold changed in section 4.2. I found the current procedure and cited the exact passage.',
    challengeTitle: 'Search finds files. Your team still has to assemble the answer.',
    challengeDescription:
      'A useful knowledge system retrieves the right evidence, explains it clearly, and makes the evidence easy to inspect.',
    challenges: [
      {
        icon: 'tabler:folders',
        title: 'Fragmented sources',
        body: 'Policies, guides, pages, and Q&A live across repositories with inconsistent search.',
      },
      {
        icon: 'tabler:versions',
        title: 'Stale knowledge',
        body: 'People keep using old copies after the authoritative source has changed.',
      },
      {
        icon: 'tabler:user-question',
        title: 'Expert bottlenecks',
        body: 'A few experienced people become the routing layer for every difficult question.',
      },
    ],
    capabilityTitle: 'A practical knowledge layer, from ingestion to answer',
    capabilityDescription:
      'Build, test, deploy, and improve specialized agents from the same managed knowledge foundation.',
    capabilities: [
      {
        icon: 'tabler:file-upload',
        title: 'Flexible ingestion',
        body: 'Upload files, crawl sites, add text and Q&A, or connect supported knowledge services.',
      },
      {
        icon: 'tabler:database-search',
        title: 'Grounded retrieval',
        body: 'Retrieve relevant passages before the model forms its response.',
      },
      {
        icon: 'tabler:external-link',
        title: 'Verifiable citations',
        body: 'Attach references so users can open and review the underlying evidence.',
      },
      {
        icon: 'tabler:adjustments',
        title: 'Model choice',
        body: 'Select among supported providers and tune the agent without re-ingesting the knowledge base.',
      },
    ],
    applications: [
      'Policy questions',
      'Operations manuals',
      'Technical documentation',
      'Regulatory research',
      'Faculty knowledge',
      'Customer self-service',
    ],
    faqs: [
      {
        question: 'What can I use as a knowledge source?',
        answer:
          'Chat.co supports document uploads, website crawling, text, curated Q&A, and supported connections such as Google Drive, OneDrive, SharePoint, and Notion.',
      },
      {
        question: 'Do citations guarantee a correct answer?',
        answer:
          'No. Citations reduce risk and make the answer easier to inspect, but users should verify the cited source when a decision is consequential.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'Internal AI knowledge base', href: '/internal-ai-knowledge-base' },
      { label: 'AI chatbot for website', href: '/ai-chatbot-for-website' },
      { label: 'Citation quality guide', href: '/docs/citation-quality' },
    ],
  },
  internalAiKnowledgeBase: {
    path: '/internal-ai-knowledge-base',
    breadcrumb: 'Internal AI Knowledge Base',
    eyebrow: 'Private internal expert',
    title: 'Give your team one private place to ask company knowledge',
    description:
      'Build an access-restricted AI expert over approved policies, manuals, project files, and connected knowledge—then return answers with citations.',
    seoTitle: 'Internal AI Knowledge Base for Teams | Chat.co',
    seoDescription:
      'Create a private internal AI knowledge base with access controls, connected sources, and cited answers for employees and specialist teams.',
    heroBullets: [
      'Restrict access to approved users',
      'Connect common file and knowledge tools',
      'Preserve institutional knowledge with cited answers',
    ],
    sampleQuestion: 'What is the approval path for a high-risk vendor?',
    sampleAnswer:
      'The current policy requires Legal and Security review before Finance approval. I cited each relevant section.',
    challengeTitle:
      'Internal knowledge is valuable only when the right person can find the current answer.',
    challengeDescription:
      'Folders and wikis store information; an internal expert helps employees use it without bypassing access controls.',
    challenges: [
      {
        icon: 'tabler:lock-search',
        title: 'Access boundaries',
        body: 'Sensitive material cannot be exposed through the same experience used for public visitors.',
      },
      {
        icon: 'tabler:route',
        title: 'Slow onboarding',
        body: 'New employees rely on tribal knowledge and repeated interruptions to learn routine processes.',
      },
      {
        icon: 'tabler:brain',
        title: 'Knowledge loss',
        body: 'Critical context disappears when senior employees change roles or leave.',
      },
    ],
    capabilityTitle: 'An internal expert built around approved knowledge',
    capabilityDescription:
      'Separate the private employee experience from public-facing agents while managing both on Chat.co.',
    capabilities: [
      {
        icon: 'tabler:user-shield',
        title: 'Restricted access',
        body: 'Invite approved users and use authentication controls for private agents.',
      },
      {
        icon: 'tabler:cloud-data-connection',
        title: 'Connected knowledge',
        body: 'Bring in supported sources such as Drive, OneDrive, SharePoint, and Notion.',
      },
      {
        icon: 'tabler:history',
        title: 'Current source material',
        body: 'Update the underlying knowledge without rebuilding the employee experience.',
      },
      {
        icon: 'tabler:messages',
        title: 'Conversation visibility',
        body: 'Review conversations to find unanswered questions and knowledge gaps.',
      },
    ],
    applications: [
      'Employee policies',
      'Sales enablement',
      'Operations procedures',
      'Client matter files',
      'Faculty portals',
      'Technical runbooks',
    ],
    faqs: [
      {
        question: 'Can an internal agent be kept private?',
        answer:
          'Yes. Chat.co supports authenticated agents restricted to invited or approved users, along with role-based administration controls.',
      },
      {
        question: 'Can we also run a public website agent?',
        answer:
          'Yes. Public website agents and access-restricted internal experts can be managed on the same platform with separate knowledge and instructions.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'AI knowledge base', href: '/ai-knowledge-base' },
      { label: 'Education solution', href: '/solutions/education' },
      { label: 'Enterprise SSO guide', href: '/docs/enterprise-sso' },
    ],
  },
  customerSupportAiAgent: {
    path: '/customer-support-ai-agent',
    breadcrumb: 'Customer Support AI Agent',
    eyebrow: 'Customer support AI agent',
    title: 'Resolve routine support questions with answers customers can verify',
    description:
      'Ground a customer-facing agent in your help center, product docs, and policies. Provide 24/7 first-line answers, capture context, and support escalation to your team.',
    seoTitle: 'Customer Support AI Agent with Citations | Chat.co',
    seoDescription:
      'Build a customer support AI agent grounded in your help center and product documentation, with citations, lead capture, and human handoff.',
    heroBullets: [
      'Answer from your approved support content',
      'Serve customers outside business hours',
      'Review conversations and identify knowledge gaps',
    ],
    sampleQuestion: 'How do I change my account owner without losing data?',
    sampleAnswer:
      'Follow these three steps. Your data remains in the workspace; here is the account-transfer guide I used.',
    challengeTitle:
      'Support volume grows faster than the team when every answer requires a person.',
    challengeDescription:
      'Automate the repeatable layer while keeping sources visible and a route to human help.',
    challenges: [
      {
        icon: 'tabler:message-2-question',
        title: 'Repeat volume',
        body: 'A large share of conversations begins with questions already answered in existing documentation.',
      },
      {
        icon: 'tabler:moon-stars',
        title: 'Coverage gaps',
        body: 'Customers still need useful answers when the team is offline or focused on complex cases.',
      },
      {
        icon: 'tabler:chart-dots',
        title: 'Hidden content gaps',
        body: 'Without conversation review, documentation teams cannot see which questions remain unresolved.',
      },
    ],
    capabilityTitle: 'Make your support content conversational',
    capabilityDescription:
      'Start with self-service answers and maintain a clear path to people when a conversation needs judgment.',
    capabilities: [
      {
        icon: 'tabler:lifebuoy',
        title: 'First-line resolution',
        body: 'Respond instantly to common product, process, and policy questions.',
      },
      {
        icon: 'tabler:books',
        title: 'Help-center grounding',
        body: 'Use approved articles, websites, documents, and curated Q&A as the answer base.',
      },
      {
        icon: 'tabler:user-up',
        title: 'Human handoff',
        body: 'Support escalation workflows when an agent should not complete the conversation alone.',
      },
      {
        icon: 'tabler:report-analytics',
        title: 'Conversation review',
        body: 'Inspect questions and responses to improve both the agent and the source content.',
      },
    ],
    applications: [
      'Account questions',
      'Product setup',
      'Returns and policies',
      'Troubleshooting',
      'Lead capture',
      'Customer onboarding',
    ],
    faqs: [
      {
        question: 'Will the agent replace the support team?',
        answer:
          'The best fit is first-line, repeatable support. Teams remain responsible for complex, sensitive, or account-specific decisions and can provide a handoff path.',
      },
      {
        question: 'Can I see what customers ask?',
        answer:
          'Yes. Conversation logs help teams review answer quality, identify missing content, and improve the knowledge base.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'AI chatbot for website', href: '/ai-chatbot-for-website' },
      { label: 'Technology & SaaS solution', href: '/solutions/saas' },
      { label: 'Compare Chatbase', href: '/compare/chatbase' },
    ],
  },
  compareChatbase: {
    path: '/compare/chatbase',
    breadcrumb: 'Chat.co vs Chatbase',
    eyebrow: 'Chat.co vs Chatbase',
    title: 'Compare two different approaches to customer-facing AI agents',
    description:
      'Chatbase publicly centers customer-facing support and sales agents. Chat.co combines website agents with private internal experts and source-backed research workflows.',
    seoTitle: 'Chat.co vs Chatbase: AI Agent Comparison',
    seoDescription:
      'Compare Chat.co and Chatbase by primary use case, deployment, citations, internal knowledge workflows, and evaluation criteria.',
    heroBullets: [
      'Side-by-side product focus without manufactured feature wins',
      'Official competitor source linked for verification',
      'A repeatable proof-of-concept checklist',
    ],
    sampleQuestion: 'Which product fits both public support and private research?',
    sampleAnswer:
      'Evaluate Chat.co when one team needs both surfaces. If customer-facing support is the entire program, include Chatbase in the test.',
    challengeTitle: 'The right shortlist starts with the job, not a logo grid.',
    challengeDescription:
      'Both products can answer from business content, but their public positioning and product center of gravity differ.',
    challenges: [
      {
        icon: 'tabler:target-arrow',
        title: 'Different centers',
        body: 'Chatbase emphasizes customer-facing agents; Chat.co spans public and private knowledge agents.',
      },
      {
        icon: 'tabler:checklist',
        title: 'Feature-list noise',
        body: 'A long checklist can hide the workflows, access rules, and evidence quality that actually matter.',
      },
      {
        icon: 'tabler:refresh-dot',
        title: 'Products change',
        body: 'Current limits, pricing, and integrations should be verified directly before a purchase.',
      },
    ],
    capabilityTitle: 'Where Chat.co earns a place in the evaluation',
    capabilityDescription:
      'Test Chat.co when your roadmap extends beyond a website support widget.',
    capabilities: [
      {
        icon: 'tabler:world',
        title: 'Public agents',
        body: 'Embed customer-facing agents on a website and ground them in approved content.',
      },
      {
        icon: 'tabler:lock',
        title: 'Private experts',
        body: 'Deploy authenticated knowledge experiences for employees and invited users.',
      },
      {
        icon: 'tabler:quote',
        title: 'Cited research',
        body: 'Use source references for document-heavy operational and research workflows.',
      },
      {
        icon: 'tabler:arrows-exchange',
        title: 'Model choice',
        body: 'Choose among supported model providers without rebuilding the knowledge base.',
      },
    ],
    applications: [
      'Website support',
      'Lead qualification',
      'Internal policy help',
      'Document research',
      'Customer onboarding',
      'Specialist knowledge agents',
    ],
    comparison: {
      competitor: 'Chatbase',
      officialUrl: 'https://www.chatbase.co/features/product-overview',
      sourceLabel: 'Chatbase official product overview',
      reviewed: 'August 26, 2026',
      rows: [
        {
          topic: 'Public positioning',
          chatCo:
            'Customer-facing agents, private internal experts, and specialized research agents.',
          competitor: 'Customer-facing AI agents for support, sales, and related workflows.',
        },
        {
          topic: 'Private knowledge use',
          chatCo: 'Access-restricted agents are a first-class product shape.',
          competitor:
            'Verify the current private/internal deployment fit against your access requirements.',
        },
        {
          topic: 'Grounding and citations',
          chatCo: 'Grounded answers with source references for verification.',
          competitor: 'Grounding is central; test citation behavior with your own source set.',
        },
        {
          topic: 'Deployment',
          chatCo: 'Website embed, iframe, share link, QR code, API, and documented MCP access.',
          competitor: 'Website deployment plus its published integrations and agent actions.',
        },
        {
          topic: 'Best evaluation',
          chatCo: 'One platform for public support plus internal or research workflows.',
          competitor: 'A focused customer-facing agent program.',
        },
      ],
    },
    faqs: [
      {
        question: 'Is Chat.co always better than Chatbase?',
        answer:
          'No product is best for every program. Run the same representative questions, documents, access rules, and success criteria in both products.',
      },
      {
        question: 'Does this page include live competitor pricing?',
        answer:
          'No. Pricing and limits change frequently. Follow the linked official source and each vendor’s pricing page before making a purchase decision.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'Customer support AI agent', href: '/customer-support-ai-agent' },
      { label: 'Internal AI knowledge base', href: '/internal-ai-knowledge-base' },
      { label: 'Compare CustomGPT.ai', href: '/compare/customgpt' },
    ],
  },
  compareCustomGpt: {
    path: '/compare/customgpt',
    breadcrumb: 'Chat.co vs CustomGPT.ai',
    eyebrow: 'Chat.co vs CustomGPT.ai',
    title: 'Compare document-grounded agents by deployment and operating model',
    description:
      'CustomGPT.ai publishes detailed document-agent and citation controls. Chat.co pairs cited document answers with public website agents, private internal experts, and visible model-provider choice.',
    seoTitle: 'Chat.co vs CustomGPT.ai: AI Agent Comparison',
    seoDescription:
      'Compare Chat.co and CustomGPT.ai by citations, public and private deployment, model strategy, and the workflows each product emphasizes.',
    heroBullets: [
      'Citation workflows compared directly',
      'Public and private deployment considered together',
      'Official CustomGPT.ai documentation linked',
    ],
    sampleQuestion: 'Which product should I test for cited document answers?',
    sampleAnswer:
      'Both belong on that shortlist. Test retrieval quality, citation usefulness, permissions, deployment, and administration with the same source set.',
    challengeTitle: '“Chat with your documents” is a category, not a complete evaluation.',
    challengeDescription:
      'The decision depends on who will use the agent, where it will live, how evidence appears, and who maintains it.',
    challenges: [
      {
        icon: 'tabler:quote',
        title: 'Citation detail',
        body: 'Teams need to inspect how citations are presented and whether they point to useful evidence.',
      },
      {
        icon: 'tabler:users',
        title: 'Audience mix',
        body: 'A public visitor experience and a private employee expert impose different access and design requirements.',
      },
      {
        icon: 'tabler:tool',
        title: 'Operating effort',
        body: 'Administration, source updates, deployment, and review matter after the initial demo.',
      },
    ],
    capabilityTitle: 'Where Chat.co is differentiated in the test',
    capabilityDescription:
      'Evaluate the combined platform when cited research is one of several agent experiences you need.',
    capabilities: [
      {
        icon: 'tabler:layout-dashboard',
        title: 'Multiple agent shapes',
        body: 'Create website, internal, and specialist agents in one no-code environment.',
      },
      {
        icon: 'tabler:brand-open-source',
        title: 'Inspect the evidence',
        body: 'Attach sources to grounded answers so users can review the underlying material.',
      },
      {
        icon: 'tabler:brain',
        title: 'Provider choice',
        body: 'Use supported models from multiple providers without rebuilding source knowledge.',
      },
      {
        icon: 'tabler:plug-connected',
        title: 'Flexible deployment',
        body: 'Use embeds, links, QR codes, API access, and documented MCP workflows.',
      },
    ],
    applications: [
      'Document Q&A',
      'Internal research',
      'Website answers',
      'Regulatory records',
      'Policy guidance',
      'Specialist portals',
    ],
    comparison: {
      competitor: 'CustomGPT.ai',
      officialUrl: 'https://docs.customgpt.ai/docs/how-to-change-the-response-source-1',
      sourceLabel: 'CustomGPT.ai official citation documentation',
      reviewed: 'August 26, 2026',
      rows: [
        {
          topic: 'Public positioning',
          chatCo: 'No-code knowledge agents for customers, teams, and specialist research.',
          competitor:
            'Document-grounded custom agents with published citation and source controls.',
        },
        {
          topic: 'Citation presentation',
          chatCo: 'Source references attached to grounded answers.',
          competitor: 'Publishes controls for inline and end-of-answer citation formats.',
        },
        {
          topic: 'Audience and access',
          chatCo: 'Public website agents and authenticated internal experts.',
          competitor:
            'Public and private deployments; verify the exact access model for your program.',
        },
        {
          topic: 'Model approach',
          chatCo: 'Visible choice among supported AI model providers.',
          competitor: 'Verify current model options and controls in the product documentation.',
        },
        {
          topic: 'Best evaluation',
          chatCo: 'Teams combining customer, employee, and research experiences.',
          competitor: 'Teams prioritizing a mature document-agent and citation workflow.',
        },
      ],
    },
    faqs: [
      {
        question: 'Does CustomGPT.ai support citations?',
        answer:
          'Yes. Its official documentation describes inline and end-of-answer citation formats. Any evaluation should test citation accuracy and usefulness with your own documents.',
      },
      {
        question: 'Does this page rank the two products?',
        answer:
          'No. It identifies differences worth testing. The right choice depends on your source set, audience, access model, deployment, and operating requirements.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'AI knowledge base', href: '/ai-knowledge-base' },
      { label: 'Internal AI knowledge base', href: '/internal-ai-knowledge-base' },
      { label: 'Compare Chatbase', href: '/compare/chatbase' },
    ],
  },
  energy: {
    path: '/solutions/energy',
    breadcrumb: 'Energy AI Solution',
    eyebrow: 'Chat.co for energy & utilities',
    title: 'Research rate cases and regulatory records with the citation attached',
    description:
      'Give analysts an expert agent over filings, orders, testimony, transcripts, and internal guidance—organized by docket or regulatory domain.',
    seoTitle: 'AI Regulatory Research for Energy & Utilities | Chat.co',
    seoDescription:
      'Build cited AI research agents for energy rate cases, utility filings, commission orders, testimony, and regulatory knowledge.',
    heroBullets: [
      'Dedicated expert agent by docket or domain',
      'Answers grounded in the record you approve',
      'Source citations for analyst verification',
    ],
    sampleQuestion: 'What precedent did the commission use for this cost allocation?',
    sampleAnswer:
      'The order relied on the allocation method in section 6 and cited two earlier proceedings. Here are all three record sources.',
    challengeTitle:
      'Regulatory records are too large for manual recall and too consequential for unsupported answers.',
    challengeDescription:
      'Analysts need a faster path from a question to the exact language in the record.',
    challenges: [
      {
        icon: 'tabler:files',
        title: 'Massive dockets',
        body: 'A proceeding can span filings, testimony, exhibits, transcripts, and orders across thousands of pages.',
      },
      {
        icon: 'tabler:gavel',
        title: 'Precedent matters',
        body: 'The value of an answer depends on the jurisdiction, proceeding, and exact source language.',
      },
      {
        icon: 'tabler:clock',
        title: 'Research drag',
        body: 'Senior analysts spend hours locating passages before analysis and drafting can begin.',
      },
    ],
    capabilityTitle: 'Build a regulatory expert around the record',
    capabilityDescription:
      'Segment knowledge by docket or domain, ask in plain language, and move from answer to source.',
    capabilities: [
      {
        icon: 'tabler:folder-search',
        title: 'Docket-specific agents',
        body: 'Keep each proceeding or regulatory domain focused on its approved record.',
      },
      {
        icon: 'tabler:file-certificate',
        title: 'Record citations',
        body: 'Trace material answers to the underlying filing, order, testimony, or transcript.',
      },
      {
        icon: 'tabler:users-group',
        title: 'Shared expertise',
        body: 'Make institutional research accessible beyond the analyst who first completed it.',
      },
      {
        icon: 'tabler:lock',
        title: 'Controlled access',
        body: 'Restrict specialist agents to the teams and users who should see the material.',
      },
    ],
    applications: [
      'Rate case research',
      'Commission precedent',
      'Testimony review',
      'Tariff questions',
      'Operational procedures',
      'Regulatory onboarding',
    ],
    proof: maxetaProof,
    faqs: [
      {
        question: 'Can each docket have a separate agent?',
        answer:
          'Yes. Teams can create dedicated agents for a docket, jurisdiction, or subject area so the approved source set stays focused.',
      },
      {
        question: 'Is the output legal or regulatory advice?',
        answer:
          'No. Chat.co helps retrieve and explain source material. Qualified professionals remain responsible for analysis, advice, filings, and decisions.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'Energy & Utilities use case', href: '/use-cases/energy' },
      { label: 'MAXeta case study', href: '/case-studies/maxeta-energy' },
      { label: 'AI knowledge base', href: '/ai-knowledge-base' },
    ],
  },
  education: {
    path: '/solutions/education',
    breadcrumb: 'Education AI Solution',
    eyebrow: 'Chat.co for education',
    title: 'Give faculty, staff, students, and families answers from current school sources',
    description:
      'Create an authenticated Faculty Portal and a separate public website agent from handbooks, policies, procedures, and approved institutional knowledge.',
    seoTitle: 'AI Knowledge Portals for Schools & Education | Chat.co',
    seoDescription:
      'Build a private Faculty Portal and public school website agent from handbooks and policies, with cited answers and controlled access.',
    heroBullets: [
      'Private Faculty Portal for staff knowledge',
      'Public agent for parents and prospective families',
      'Citations to the current handbook or policy',
    ],
    sampleQuestion: 'What is the current procedure for an early dismissal?',
    sampleAnswer:
      'The current handbook requires these steps. I cited the exact section so you can confirm the policy.',
    challengeTitle:
      'A school cannot operate from binders, stale copies, and repeated inbox questions.',
    challengeDescription:
      'Different audiences need different access, but every answer should come from current institutional knowledge.',
    challenges: [
      {
        icon: 'tabler:books',
        title: 'Manual overload',
        body: 'Faculty policies and procedures are difficult to search and costly to reprint after updates.',
      },
      {
        icon: 'tabler:mail-question',
        title: 'Repeated inquiries',
        body: 'Administrators answer the same operational and family questions across phone, email, and meetings.',
      },
      {
        icon: 'tabler:versions',
        title: 'Stale copies',
        body: 'Old files and printed manuals remain in circulation after a policy changes.',
      },
    ],
    capabilityTitle: 'One knowledge foundation, separate experiences for each audience',
    capabilityDescription:
      'Keep staff knowledge private while publishing approved answers for families and prospective students.',
    capabilities: [
      {
        icon: 'tabler:school',
        title: 'Faculty Portal',
        body: 'Give authenticated faculty a conversational interface to manuals, policies, and procedures.',
      },
      {
        icon: 'tabler:home-question',
        title: 'Family answers',
        body: 'Answer admissions, schedule, policy, and program questions on the public website.',
      },
      {
        icon: 'tabler:language',
        title: 'Multilingual access',
        body: 'Help users interact with approved school knowledge in the language they use.',
      },
      {
        icon: 'tabler:refresh',
        title: 'Update once',
        body: 'Replace the source material so future answers draw from the current version.',
      },
    ],
    applications: [
      'Faculty manuals',
      'Admissions questions',
      'Emergency procedures',
      'Academic policies',
      'Parent support',
      'Accreditation evidence',
    ],
    proof: calvarioProof,
    faqs: [
      {
        question: 'Can faculty and parents see different information?',
        answer:
          'Yes. Create separate agents with different knowledge sources, instructions, and access rules for faculty, families, or other audiences.',
      },
      {
        question: 'Does Chat.co replace the school’s official policy documents?',
        answer:
          'No. The source documents remain authoritative. Citations help users open and verify the policy behind an answer.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'Education use case', href: '/use-cases/education' },
      { label: 'Calvario case study', href: '/case-studies/calvario-academy' },
      { label: 'Chat.co for Schools', href: '/schools' },
    ],
  },
  legal: {
    path: '/solutions/legal',
    breadcrumb: 'Legal & Regulatory AI Solution',
    eyebrow: 'Chat.co for legal & regulatory teams',
    title: 'Find the controlling document, clause, filing, or precedent—and cite it',
    description:
      'Create a private knowledge agent over approved matter files, agreements, briefs, policies, and regulatory records for faster source-backed research.',
    seoTitle: 'AI Knowledge Agents for Legal & Regulatory Teams | Chat.co',
    seoDescription:
      'Build private, cited AI knowledge agents for legal matter files, contracts, policies, filings, precedents, and regulatory research.',
    heroBullets: [
      'Access-restricted agents for approved teams',
      'Answers linked to underlying matter and regulatory sources',
      'Separate agents by client, matter, or domain',
    ],
    sampleQuestion: 'Which provisions govern notice after a material control change?',
    sampleAnswer:
      'Sections 8.3 and 11.1 address notice timing and recipients. I cited both clauses for review.',
    challengeTitle:
      'Professional judgment should start with the right evidence, not a folder hunt.',
    challengeDescription:
      'Legal and regulatory teams need faster retrieval without losing the source trail or confusing software output with advice.',
    challenges: [
      {
        icon: 'tabler:folder-code',
        title: 'Matter sprawl',
        body: 'Relevant language is distributed across agreements, correspondence, filings, and internal work product.',
      },
      {
        icon: 'tabler:scale',
        title: 'Context sensitivity',
        body: 'A useful answer depends on the exact matter, jurisdiction, document version, and governing source.',
      },
      {
        icon: 'tabler:shield-lock',
        title: 'Confidentiality',
        body: 'Client and internal material requires intentional access boundaries and controlled deployment.',
      },
    ],
    capabilityTitle: 'A private research layer over the sources your team approves',
    capabilityDescription:
      'Move from a question to relevant text and make the evidence easy for a professional to review.',
    capabilities: [
      {
        icon: 'tabler:briefcase',
        title: 'Matter-specific experts',
        body: 'Create focused agents by client, matter, regulatory domain, or internal practice area.',
      },
      {
        icon: 'tabler:section-sign',
        title: 'Source-backed answers',
        body: 'Return citations to relevant clauses, filings, policies, and other underlying documents.',
      },
      {
        icon: 'tabler:user-lock',
        title: 'Restricted access',
        body: 'Limit private agents to approved users and manage administrative roles.',
      },
      {
        icon: 'tabler:message-search',
        title: 'Knowledge-gap review',
        body: 'Use conversation history to see where the approved source set needs improvement.',
      },
    ],
    applications: [
      'Matter-file research',
      'Contract questions',
      'Policy interpretation',
      'Regulatory filings',
      'Precedent retrieval',
      'Professional onboarding',
    ],
    proof: sosaArveloProof,
    faqs: [
      {
        question: 'Does Chat.co provide legal advice?',
        answer:
          'No. Chat.co is a knowledge and retrieval platform. Licensed professionals remain responsible for legal analysis, advice, privilege, confidentiality, and final decisions.',
      },
      {
        question: 'Can we isolate knowledge by matter or client?',
        answer:
          'Yes. Teams can create separate agents and approved source sets for different matters, clients, or regulatory domains.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'Legal & Law Firms use case', href: '/use-cases/legal' },
      { label: 'Energy regulatory solution', href: '/solutions/energy' },
      { label: 'Internal AI knowledge base', href: '/internal-ai-knowledge-base' },
    ],
  },
  saas: {
    path: '/solutions/saas',
    breadcrumb: 'SaaS AI Solution',
    eyebrow: 'Chat.co for technology & SaaS',
    title: 'Make product docs answer customers and enable your team',
    description:
      'Turn help centers, API references, release notes, runbooks, and internal enablement material into specialized agents for support, onboarding, and product knowledge.',
    seoTitle: 'AI Support & Knowledge Agents for SaaS | Chat.co',
    seoDescription:
      'Build AI agents for SaaS support, product documentation, API references, onboarding, and internal enablement with source citations.',
    heroBullets: [
      'Website agent grounded in current product docs',
      'Private expert for support and success teams',
      'Citations to help articles and technical references',
    ],
    sampleQuestion: 'How do I rotate an API key without interrupting production?',
    sampleAnswer:
      'Create the new key, update the environment, verify traffic, then revoke the old key. Here is the rotation guide.',
    challengeTitle:
      'Fast-moving products create a documentation and support synchronization problem.',
    challengeDescription:
      'Customers and teams need the current answer across releases, integrations, API changes, and account policies.',
    challenges: [
      {
        icon: 'tabler:rocket',
        title: 'Rapid change',
        body: 'Releases and documentation updates quickly make older support answers obsolete.',
      },
      {
        icon: 'tabler:code',
        title: 'Technical depth',
        body: 'API and integration questions require precise references, not a generic conversational response.',
      },
      {
        icon: 'tabler:users-plus',
        title: 'Scaling onboarding',
        body: 'Customers and new employees repeat the same setup and workflow questions.',
      },
    ],
    capabilityTitle: 'One product knowledge layer for customers and teams',
    capabilityDescription:
      'Create separate agent experiences while grounding each one in the appropriate approved sources.',
    capabilities: [
      {
        icon: 'tabler:headset',
        title: 'Customer support',
        body: 'Handle repeatable product questions on the website before they become tickets.',
      },
      {
        icon: 'tabler:api',
        title: 'Developer answers',
        body: 'Ground technical responses in API references, integration guides, and release notes.',
      },
      {
        icon: 'tabler:users',
        title: 'Internal enablement',
        body: 'Give support, success, and sales a private expert over approved product knowledge.',
      },
      {
        icon: 'tabler:webhook',
        title: 'Application workflows',
        body: 'Use documented APIs, webhooks, and deployment options to connect agent experiences.',
      },
    ],
    applications: [
      'Help-center deflection',
      'API documentation',
      'Implementation support',
      'Customer onboarding',
      'Release-note questions',
      'Internal enablement',
    ],
    faqs: [
      {
        question: 'Can we create separate agents for customers and employees?',
        answer:
          'Yes. Use a public website agent for customers and an authenticated internal expert for support, success, sales, or engineering teams.',
      },
      {
        question: 'How do we keep answers current after a release?',
        answer:
          'Update or recrawl the approved source material. Teams should review conversations after significant releases to identify stale answers and missing documentation.',
      },
      sharedFaqs.trial,
    ],
    related: [
      { label: 'Technology & SaaS use case', href: '/use-cases/technology' },
      { label: 'Customer support AI agent', href: '/customer-support-ai-agent' },
      { label: 'API documentation', href: '/api-docs' },
    ],
  },
} satisfies Record<string, SearchLandingPageConfig>;
